/**
 * Состояние аккаунта, каким его видит сервер.
 *
 * Экран аккаунта показывал только итог последнего действия в этой вкладке:
 * зарегистрировались — увидели, что письмо ушло; перезагрузили страницу —
 * снова пустая форма, будто ничего не было. Подтверждён ли адрес и идёт ли
 * trial, приложение не знало, потому что не спрашивало.
 */

import type { AccountsGateway, SignedIn } from '../contracts/registration.js';

/**
 * Возвращает состояние вошедшего пользователя либо `null`, если сессии нет.
 *
 * Перенос прогресса здесь не выполняется: он происходит при входе и при
 * подтверждении адреса, а этот вызов только читает (`progress: 'none'`).
 */
export async function readSignedIn(gateway: AccountsGateway): Promise<SignedIn | null> {
  const session = await gateway.currentSession();
  if (session === null) return null;

  const trial = await gateway.trialStatus();

  return {
    kind: 'signed-in',
    userId: session.userId,
    email: session.email,
    emailVerified: session.emailVerified,
    progress: 'none',
    trialDaysLeft: trial.daysLeft,
    trialNotice: trial.notice,
  };
}

/**
 * Забирает подтверждение, сделанное на другом устройстве.
 *
 * Возвращает состояние вошедшего, если адрес уже подтверждён: сервер по
 * секрету открывает сессию именно этому браузеру. `null` — подтверждения ещё
 * нет либо секрет ничей; в обоих случаях ответ одинаков, и различить их
 * приложение не может и не должно.
 */
export async function claimConfirmedRegistration(
  gateway: AccountsGateway,
  claimToken: string,
): Promise<SignedIn | null> {
  const session = await gateway.registrationStatus(claimToken);
  if (session === null) return null;

  const trial = await gateway.trialStatus();

  return {
    kind: 'signed-in',
    userId: session.userId,
    email: session.email,
    emailVerified: session.emailVerified,
    progress: 'none',
    trialDaysLeft: trial.daysLeft,
    trialNotice: trial.notice,
  };
}
